import { supabase, supabaseUrl } from '@/lib/supabase';
import {
  type FoundContactEmailSource,
  type FoundContactEmailStatus,
  type OutreachEnrollmentStatus,
  type OutreachMessageStatus,
  type OutreachSettings,
  type OutreachStep,
  parseOutreachSettings,
  parseOutreachSteps,
} from '@shared/outreach';

/**
 * « Trouver et contacter » (03/11/2026): what the sequence page reads (under
 * RLS, live) and the gestures it sends to the `outreach` function.
 */

export interface FoundContact {
  id: string;
  appRecordId: string;
  fullName: string;
  jobTitle: string | null;
  origin: 'crm' | 'dossier' | 'web' | 'manual';
  sourceUrl: string | null;
  sourceQuote: string | null;
  reason: string | null;
  rank: number;
  state: 'proposed' | 'chosen' | 'dismissed';
  email: string | null;
  emailStatus: FoundContactEmailStatus | null;
  emailSource: FoundContactEmailSource | null;
  emailMissing: 'not_found' | 'no_provider' | 'opposed' | null;
  phone: string | null;
  phoneMissing: 'not_found' | 'no_provider' | null;
}

export interface OutreachMessage {
  id: string;
  stepIndex: number;
  subject: string;
  body: string;
  status: OutreachMessageStatus;
  scheduledFor: string | null;
  sentAt: string | null;
  error: string | null;
}

export interface OutreachEnrollment {
  id: string;
  sequenceId: string;
  appRecordId: string;
  foundContactId: string;
  ownerUserId: string;
  toEmail: string;
  status: OutreachEnrollmentStatus;
  stopReason: string | null;
  messages: OutreachMessage[];
}

export interface OutreachJobState {
  kind: 'find_people' | 'find_email' | 'find_phone' | 'write';
  subjectId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  error: string | null;
}

/** A campaign (04/11/2026): a name, its sender, its steps and settings, the objects of the list it holds. */
export interface OutreachCampaign {
  id: string;
  name: string;
  ownerUserId: string;
  steps: OutreachStep[];
  settings: OutreachSettings;
  recordIds: string[];
  createdAt: string;
}

export interface OutreachState {
  campaigns: OutreachCampaign[];
  contacts: FoundContact[];
  enrollments: OutreachEnrollment[];
  jobs: OutreachJobState[];
}

type Row = Record<string, unknown>;
const str = (value: unknown): string | null => typeof value === 'string' ? value : null;

const toContact = (row: Row): FoundContact => ({
  id: String(row.id), appRecordId: String(row.app_record_id), fullName: String(row.full_name), jobTitle: str(row.job_title),
  origin: row.origin as FoundContact['origin'], sourceUrl: str(row.source_url), sourceQuote: str(row.source_quote), reason: str(row.reason),
  rank: Number(row.rank ?? 1), state: row.state as FoundContact['state'], email: str(row.email),
  emailStatus: str(row.email_status) as FoundContactEmailStatus | null, emailSource: str(row.email_source) as FoundContactEmailSource | null,
  emailMissing: str(row.email_missing) as FoundContact['emailMissing'], phone: str(row.phone), phoneMissing: str(row.phone_missing) as FoundContact['phoneMissing'],
});

const toMessage = (row: Row): OutreachMessage => ({
  id: String(row.id), stepIndex: Number(row.step_index), subject: String(row.subject ?? ''), body: String(row.body ?? ''),
  status: row.status as OutreachMessageStatus, scheduledFor: str(row.scheduled_for), sentAt: str(row.sent_at), error: str(row.error),
});

/** The last job of each kind and subject, the newest first: an old failure does not hide a new search. */
export const latestJobs = (jobs: OutreachJobState[]): OutreachJobState[] => {
  const seen = new Set<string>();
  return jobs.filter((job) => {
    const key = `${job.kind}:${job.subjectId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Everything of one app's campaigns page: every campaign of the app, each its sender's. */
export const loadOutreachState = async (toolAppId: string): Promise<OutreachState> => {
  const [sequences, members, contacts, enrollments, jobs] = await Promise.all([
    supabase.from('outreach_sequences').select('id, name, owner_user_id, steps, settings, created_at').eq('tool_app_id', toolAppId).order('created_at', { ascending: false }),
    supabase.from('outreach_campaign_records').select('sequence_id, app_record_id').eq('tool_app_id', toolAppId),
    supabase.from('found_contacts').select('*').eq('tool_app_id', toolAppId).order('rank'),
    supabase.from('outreach_enrollments')
      .select('id, sequence_id, app_record_id, found_contact_id, owner_user_id, to_email, status, stop_reason, outreach_messages(id, step_index, subject, body, status, scheduled_for, sent_at, error)')
      .eq('tool_app_id', toolAppId).order('created_at', { ascending: false }),
    // A finished search for people is kept too: one that found nobody must say so (04/11).
    supabase.from('outreach_jobs').select('kind, subject_id, status, error, created_at').eq('tool_app_id', toolAppId)
      .or('status.in.(queued,running,failed),and(kind.eq.find_people,status.eq.done)').order('created_at', { ascending: false }),
  ]);
  for (const result of [sequences, members, contacts, enrollments, jobs]) {
    if (result.error) throw new Error('Les campagnes n’ont pas pu être chargées.');
  }
  const recordsOf = new Map<string, string[]>();
  for (const row of (members.data ?? []) as Row[]) {
    const list = recordsOf.get(String(row.sequence_id)) ?? [];
    list.push(String(row.app_record_id));
    recordsOf.set(String(row.sequence_id), list);
  }
  return {
    campaigns: ((sequences.data ?? []) as Row[]).map((row) => ({
      id: String(row.id), name: String(row.name ?? ''), ownerUserId: String(row.owner_user_id), createdAt: String(row.created_at),
      steps: parseOutreachSteps(row.steps), settings: parseOutreachSettings(row.settings), recordIds: recordsOf.get(String(row.id)) ?? [],
    })),
    contacts: ((contacts.data ?? []) as Row[]).map(toContact),
    enrollments: ((enrollments.data ?? []) as Row[]).map((row) => ({
      id: String(row.id), sequenceId: String(row.sequence_id), appRecordId: String(row.app_record_id), foundContactId: String(row.found_contact_id),
      ownerUserId: String(row.owner_user_id), toEmail: String(row.to_email), status: row.status as OutreachEnrollmentStatus,
      stopReason: str(row.stop_reason),
      messages: (Array.isArray(row.outreach_messages) ? row.outreach_messages as Row[] : []).map(toMessage).sort((a, b) => a.stepIndex - b.stepIndex),
    })),
    jobs: latestJobs(((jobs.data ?? []) as Row[]).map((row) => ({
      kind: row.kind as OutreachJobState['kind'], subjectId: String(row.subject_id), status: row.status as OutreachJobState['status'], error: str(row.error),
    }))),
  };
};

/** Live: a campaign, a person found, an address, an e-mail written or sent changes the page. */
export const watchOutreach = (toolAppId: string, onChange: () => void): (() => void) => {
  const channel = supabase.channel(`outreach-${toolAppId}-${Math.random().toString(36).slice(2)}`);
  for (const table of ['found_contacts', 'outreach_enrollments', 'outreach_jobs', 'outreach_sequences', 'outreach_campaign_records']) {
    channel.on('postgres_changes', { event: '*', schema: 'public', table, filter: `tool_app_id=eq.${toolAppId}` }, onChange);
  }
  channel.on('postgres_changes', { event: '*', schema: 'public', table: 'outreach_messages' }, onChange);
  channel.subscribe();
  return () => { void supabase.removeChannel(channel); };
};

/** A gesture of the page; a refusal comes back with its sentence. */
export const outreachAction = async <T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('outreach', { body });
  if (error) {
    const context = (error as { context?: unknown }).context;
    const payload = context instanceof Response ? await context.json().catch(() => null) as { error?: string } | null : null;
    throw new Error(payload?.error ?? 'L’action n’a pas pu être faite.');
  }
  return data as T;
};

/** The public refusal page: no session, the token is the only key. */
export const optOut = async (token: string, action: 'read' | 'confirm') => {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const response = await fetch(`${supabaseUrl}/functions/v1/outreach-opt-out`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: anonKey, Authorization: `Bearer ${anonKey}` },
    body: JSON.stringify({ token, action }),
  });
  const payload = await response.json().catch(() => null) as { sender?: string | null; company?: string | null; email?: string; done?: boolean; error?: string } | null;
  if (!response.ok || !payload) throw new Error(payload?.error ?? 'Le lien n’a pas pu être lu.');
  return payload;
};
